import { useMemo } from "react";
import { CalendarX } from "@phosphor-icons/react";
import { absencesForDay, absenceLabel } from "../../utils/absenceUtils";
import "./AbsenceBands.css";

const toMin = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return Number.isNaN(h) ? null : h * 60 + (m || 0);
};

/**
 * Shaded "לא זמין/ה" bands for one day column (Week/Day views).
 * `absences` items may carry `canSeeDetails` (set by the consumer: admin or
 * the therapist themself) — otherwise only the generic label renders.
 * Positioned like appointments: px from FIRST_HOUR using slotHeight.
 */
export function AbsenceBands({
  absences,
  dateStr,
  firstHour,
  slotHeight,
  hoursCount,
  showName = false,
}) {
  const dayAbsences = useMemo(() => absencesForDay(absences, dateStr), [absences, dateStr]);
  if (dayAbsences.length === 0) return null;
  const totalHeight = hoursCount * slotHeight;
  const firstMinute = firstHour * 60;

  return (
    <>
      {dayAbsences.map((a) => {
        let top = 0;
        let height = totalHeight;
        if (a.allDay === false) {
          const s = toMin(a.startTime);
          const e = toMin(a.endTime);
          if (s !== null && e !== null) {
            top = Math.max(0, ((s - firstMinute) / 60) * slotHeight);
            height = Math.max(16, Math.min(((e - Math.max(s, firstMinute)) / 60) * slotHeight, totalHeight - top));
          }
        }
        const label = absenceLabel(a, { canSeeDetails: a.canSeeDetails === true, withName: showName });
        const title = a.canSeeDetails === true && a.note ? `${label} — ${a.note}` : label;
        return (
          <div
            key={a.id}
            className={`absence-band ${a.allDay === false ? "partial" : "all-day"}`}
            style={{ top: `${top}px`, height: `${height}px` }}
            title={title}
            aria-label={title}
          >
            <span className="absence-band-label">
              <CalendarX size={12} weight="fill" />
              {label}
            </span>
          </div>
        );
      })}
    </>
  );
}

/** Compact header chip: "לא זמין/ה" (+ names when several therapists). */
export function AbsenceHeaderBadge({ absences, dateStr, showName = false }) {
  const dayAbsences = useMemo(() => absencesForDay(absences, dateStr), [absences, dateStr]);
  if (dayAbsences.length === 0) return null;
  const first = dayAbsences[0];
  const text = showName
    ? dayAbsences.length === 1
      ? absenceLabel(first, { canSeeDetails: first.canSeeDetails === true, withName: true })
      : `${dayAbsences.length} לא זמינים/ות`
    : absenceLabel(first, { canSeeDetails: first.canSeeDetails === true });
  const title = dayAbsences
    .map((a) => absenceLabel(a, { canSeeDetails: a.canSeeDetails === true, withName: true }))
    .join(" · ");
  return (
    <span className="day-absence-badge" title={title}>
      {text}
    </span>
  );
}

export default AbsenceBands;
